// =============================================================================
// MiniDonut.tsx
// Compact donut chart for mobile analytics. Each slice is a stroked partial
// arc with butt linecap so adjacent slices meet at clean perpendicular cuts —
// no rounded "blobs" at slice joints (WS1 fix). Animation: stroke-dasharray
// expands from 0 to its target on mount with a per-slice delay.
// =============================================================================

import React, { useEffect, useState } from 'react';

interface MiniDonutProps {
  data: { label: string; value: number; color: string }[];
  size?: number;
}

const STROKE_WIDTH = 10;

export function MiniDonut({ data, size = 80 }: MiniDonutProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 80); return () => clearTimeout(t); }, []);

  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  // r leaves stroke-width + 4px breathing room inside the viewBox so the
  // stroke's outer edge (r + STROKE_WIDTH/2) stays well clear of the SVG
  // boundary. Previously r = (size-8)/2 left the stroke clipping by ~1px
  // at the top/bottom/sides.
  const r = (size - STROKE_WIDTH - 4) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let accumulated = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} preserveAspectRatio="xMidYMid meet" style={{ transform: 'rotate(-90deg)' }}>
      {data.map((d, i) => {
        if (d.value === 0) return null;
        const pct = d.value / total;
        const gap = 1;
        const dashLen = mounted ? Math.max(pct * circ - gap, 0) : 0;
        const dashOffset = mounted ? -(accumulated / total) * circ : 0;
        accumulated += d.value;
        return (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={STROKE_WIDTH}
            strokeDasharray={`${dashLen} ${circ - dashLen}`}
            strokeLinecap="butt"
            strokeDashoffset={dashOffset}
            opacity={0.85}
            vectorEffect="non-scaling-stroke"
            style={{ transition: `stroke-dasharray 0.9s cubic-bezier(0.25,0.46,0.45,0.94) ${i * 0.1}s, stroke-dashoffset 0.9s cubic-bezier(0.25,0.46,0.45,0.94) ${i * 0.1}s` }}
          />
        );
      })}
    </svg>
  );
}
